import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../utils/api';

/**
 * CounterOfferModal
 *
 * Replaces the browser prompt() that used to collect a counter-offer amount.
 *
 * The negotiator sets an amount, edits a drafted note that goes to the
 * provider, previews the actual email, and only then sends. Nothing is sent
 * automatically: the Send button stays disabled until the preview has been
 * generated, so no counter leaves the building that a person hasn't read.
 *
 * Three backend calls, and only the last one sends anything:
 *   GET  /counter-suggestion  drafts the note          (no write, no send)
 *   POST /counter-preview     renders the real email   (no write, no send)
 *   POST /respond             sends and records        (the only send)
 *
 * The note is deliberately qualitative. MayLogic's sample size, percentiles
 * and suggested ask are shown in the basis panel for the negotiator, but
 * they never go into the provider-facing text — that was Teresa's call on
 * 2026-10-21, and it keeps the firm's own data out of a letter a provider
 * could quote back later.
 */
function CounterOfferModal({ negotiation, close, onSent }) {
  const negId = negotiation?.id || negotiation?._id;

  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [noteTouched, setNoteTouched] = useState(false);
  const [basis, setBasis] = useState(null);

  const [drafting, setDrafting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [acked, setAcked] = useState({});
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const amountNum = parseFloat(amount);
  const amountValid = !Number.isNaN(amountNum) && amountNum > 0;

  // Any edit to the amount or the note invalidates a preview the negotiator
  // has already seen — otherwise they could preview one number and send a
  // different one.
  useEffect(() => {
    setPreview(null);
    setAcked({});
  }, [amount, note]);

  // `force` exists because setNoteTouched(false) followed by draftNote()
  // would not work: draftNote closes over the current render's
  // noteTouched, so the guard below would still see the old value and
  // silently refuse to replace the text.
  const draftNote = useCallback(async (force = false) => {
    if (!amountValid) return;
    setDrafting(true);
    setError(null);
    try {
      const r = await axios.get(
        `${API_URL}/api/negotiations/${negId}/counter-suggestion`,
        { params: { amount: amountNum }, withCredentials: true }
      );
      setBasis(r.data.basis || null);
      // Never clobber something the negotiator has written, unless they
      // explicitly asked for a redraft.
      if (force || !noteTouched) setNote(r.data.suggestion || '');
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not draft a note.');
    } finally {
      setDrafting(false);
    }
  }, [negId, amountNum, amountValid, noteTouched]);

  const runPreview = async () => {
    if (!amountValid) return;
    setPreviewing(true);
    setError(null);
    try {
      const r = await axios.post(
        `${API_URL}/api/negotiations/${negId}/counter-preview`,
        { amount: amountNum, note: note || null },
        { withCredentials: true }
      );
      setPreview(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not render a preview.');
    } finally {
      setPreviewing(false);
    }
  };

  const send = async () => {
    setSending(true);
    setError(null);
    try {
      await axios.post(
        `${API_URL}/api/negotiations/${negId}/respond`,
        {
          action: 'counter',
          amount: amountNum,
          note: note || null,
          acknowledged_rules: Object.keys(acked).filter((k) => acked[k]),
        },
        { withCredentials: true }
      );
      onSent();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to send the counter-offer.');
      setSending(false);
    }
  };

  const blocking = (preview?.violations || []).filter((v) => v.severity === 'block');
  const warnings = (preview?.violations || []).filter((v) => v.severity === 'warn');
  const allAcked = warnings.every((v) => acked[v.rule]);

  // Human in the loop: a preview must exist, it must be of the current
  // amount and note, nothing may be blocking, and every warning must have
  // been ticked.
  const canSend = amountValid && preview && blocking.length === 0 && allAcked && !sending;

  const money = (n) =>
    n === null || n === undefined ? '—'
      : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div style={s.backdrop}>
      <div style={s.modal}>
        <div style={s.title}>Counter the provider's offer</div>
        <div style={s.subtitle}>
          {negotiation?.provider_name || 'Provider'}
          {negotiation?.proposed_amount
            ? ` · they offered ${money(negotiation.proposed_amount)}`
            : ''}
        </div>

        {error && <div style={s.error}>{error}</div>}

        {/* ---- amount ---- */}
        <div style={s.section}>Your counter</div>
        <div style={{ maxWidth: 220 }}>
          <label style={s.label}>Amount</label>
          <input
            style={s.input}
            type="number"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            onBlur={() => { if (amountValid && !noteTouched) draftNote(false); }}
            placeholder="4500.00"
          />
          <div style={s.hint}>Leaving this field drafts a note you can edit.</div>
        </div>

        {/* ---- note ---- */}
        <div style={s.section}>Note to the provider</div>
        <div style={s.hint}>
          This text is sent to the provider inside the counter-offer email.
          Leave it empty and the email goes out exactly as it did before.
        </div>
        <textarea
          style={{ ...s.textarea, minHeight: 110, marginTop: 6 }}
          value={note}
          onChange={(e) => { setNote(e.target.value); setNoteTouched(true); }}
          placeholder="Why is this counter reasonable?"
        />
        <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
          <button
            style={{ ...s.cancel, opacity: amountValid && !drafting ? 1 : 0.5 }}
            onClick={() => { draftNote(true); setNoteTouched(false); }}
            disabled={!amountValid || drafting}
          >
            {drafting ? 'Drafting…' : 'Draft from MayLogic'}
          </button>
          {note && (
            <button style={s.cancel} onClick={() => { setNote(''); setNoteTouched(true); }}>
              Clear
            </button>
          )}
        </div>

        {/* ---- basis (internal only) ---- */}
        {basis && (
          <>
            <div style={s.section}>What this is based on</div>
            <div style={s.hint}>
              Internal only. None of these figures are sent to the provider.
            </div>
            <div style={{ ...s.row, marginTop: 8 }}>
              <Fact label="Settlement" value={money(basis.gross)} />
              <Fact label="Fees" value={money(basis.fees)} />
              <Fact label="Costs" value={money(basis.costs)} />
              <Fact label="Available" value={money(basis.funds_available)} />
              <Fact label="Total liens" value={money(basis.total_liens)} />
              <Fact
                label="Client nets"
                value={money(basis.client_net)}
                warn={basis.client_net !== null && basis.client_net <= 0}
              />
            </div>
            {basis.maylogic ? (
              <div style={{ ...s.row, marginTop: 10 }}>
                <Fact label="Prior matters" value={basis.maylogic.n ?? '—'} />
                <Fact label="Confidence" value={basis.maylogic.confidence || '—'} />
                <Fact
                  label="Their median"
                  value={basis.maylogic.median_reduction_pct != null
                    ? `${Math.round(basis.maylogic.median_reduction_pct * 100)}%`
                    : '—'}
                />
                <Fact label="Suggested ask" value={money(basis.maylogic.suggested_ask)} />
              </div>
            ) : (
              <div style={s.hint}>
                No MayLogic history for this provider (or the warehouse is
                unavailable). The note falls back to case figures only.
              </div>
            )}
          </>
        )}

        {/* ---- preview ---- */}
        <div style={s.section}>Review before sending</div>
        <button
          style={{ ...s.cancel, opacity: amountValid && !previewing ? 1 : 0.5 }}
          onClick={runPreview}
          disabled={!amountValid || previewing}
        >
          {previewing ? 'Rendering…' : preview ? 'Refresh preview' : 'Preview the email'}
        </button>

        {preview && (
          <>
            {blocking.map((v) => (
              <div key={v.rule} style={s.error}>
                <strong>Blocked — {v.rule}.</strong> {v.message}
              </div>
            ))}
            {warnings.map((v) => (
              <div key={v.rule} style={s.warn}>
                <div><strong>{v.rule}.</strong> {v.message}</div>
                <label style={{ ...s.checkRow, marginTop: 6 }}>
                  <input
                    type="checkbox"
                    checked={!!acked[v.rule]}
                    onChange={(e) =>
                      setAcked((a) => ({ ...a, [v.rule]: e.target.checked }))}
                  />
                  I have reviewed this and am sending anyway
                </label>
              </div>
            ))}
            <div style={s.hint}>
              Sending to {(preview.recipients || []).join(', ') || 'the provider'} ·
              subject: {preview.subject}
            </div>
            {/* sandbox="" so the email's own markup can't run anything here */}
            <iframe
              title="Counter-offer email preview"
              srcDoc={preview.html}
              sandbox=""
              style={s.frame}
            />
          </>
        )}

        <div style={s.actions}>
          <button style={s.cancel} onClick={close} disabled={sending}>Cancel</button>
          <button
            style={{ ...s.primary, opacity: canSend ? 1 : 0.5,
                     cursor: canSend ? 'pointer' : 'not-allowed' }}
            onClick={send}
            disabled={!canSend}
            title={!preview ? 'Preview the email before sending' : undefined}
          >
            {sending ? 'Sending…' : 'Send counter-offer'}
          </button>
        </div>
        {!preview && amountValid && (
          <div style={{ ...s.hint, textAlign: 'right' }}>
            Preview the email before you can send it.
          </div>
        )}
      </div>
    </div>
  );
}

function Fact({ label, value, warn }) {
  return (
    <div style={{ minWidth: 110 }}>
      <div style={s.label}>{label}</div>
      <div style={{ fontSize: 14, color: warn ? '#991b1b' : '#171D29', fontWeight: 600 }}>
        {value}
      </div>
    </div>
  );
}

const s = {
  backdrop: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.35)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 20,
  },
  modal: {
    background: 'white', borderRadius: 12, padding: '24px 28px',
    width: 'min(680px, 95vw)', maxHeight: '88vh', overflowY: 'auto',
    boxShadow: '0 12px 32px rgba(0,0,0,0.2)',
  },
  title: {
    fontFamily: "'Arial Black', Impact, sans-serif",
    fontSize: 18, color: '#171D29', marginBottom: 4,
  },
  subtitle: { fontSize: 13, color: '#6b7585', lineHeight: 1.5, marginBottom: 8 },
  error: {
    background: '#fee2e2', color: '#991b1b', padding: '8px 12px',
    borderRadius: 6, marginTop: 8, marginBottom: 8, fontSize: 12, lineHeight: 1.45,
  },
  warn: {
    background: '#fef3c7', color: '#92400e', padding: '8px 12px',
    borderRadius: 6, marginTop: 8, marginBottom: 8, fontSize: 12, lineHeight: 1.45,
  },
  section: {
    fontFamily: "'Arial Black', Impact, sans-serif",
    fontSize: 11, color: '#A1B808', letterSpacing: 1,
    textTransform: 'uppercase', margin: '16px 0 8px 0',
    borderTop: '1px solid #f3f4f6', paddingTop: 12,
  },
  row: { display: 'flex', gap: 16, flexWrap: 'wrap' },
  label: {
    display: 'block', fontSize: 10, color: '#6b7585',
    textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, marginBottom: 4,
  },
  input: {
    width: '100%', boxSizing: 'border-box', padding: '8px 10px',
    border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13,
    color: '#171D29', fontFamily: 'inherit',
  },
  textarea: {
    width: '100%', boxSizing: 'border-box', padding: '8px 10px',
    border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13,
    color: '#171D29', fontFamily: 'inherit', resize: 'vertical',
  },
  hint: { fontSize: 11, color: '#9ca3af', marginTop: 3, lineHeight: 1.4 },
  checkRow: {
    display: 'flex', alignItems: 'center', gap: 8,
    fontSize: 12, color: '#92400e', cursor: 'pointer',
  },
  frame: {
    width: '100%', height: 320, border: '1px solid #e5e7eb',
    borderRadius: 6, marginTop: 10, background: 'white',
  },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 },
  cancel: {
    padding: '8px 14px', border: '1px solid #d1d5db', background: 'white',
    color: '#171D29', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
  },
  primary: {
    padding: '8px 16px', border: '1px solid #A1B808', background: '#A1B808',
    color: '#171D29', borderRadius: 6, fontSize: 13, fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit',
  },
};

export default CounterOfferModal;
